/* ==========================================================================
   Provider Multi-Select
   PORTAL-181: Tag-style multi-select for limiting a general custom field to
   specific providers. Selected providers render as removable chips; the input
   filters the dropdown by name, email or clinic. Option rows and chips both
   show the provider's name and clinic so same-named providers stay apart.
   ========================================================================== */

import { useState } from 'react'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import { Check, ChevronDown, X } from 'lucide-react'
import type { ProviderResponse } from '@/api/types'

export interface ProviderMultiSelectProps {
  /** All providers of this pharmacy, any status */
  providers: ProviderResponse[]
  /** Currently selected provider ids */
  selectedIds: string[]
  /** Called with the new full selection */
  onChange: (ids: string[]) => void
  /** id for the combobox input, so a FormField label can reference it */
  inputId?: string
  disabled?: boolean
}

/**
 * Chip text for a selected id that no longer matches a provider. The backend
 * strips deleted providers from every assignment, so this only appears when
 * another admin deleted one after this page loaded.
 */
const REMOVED_PROVIDER_LABEL = 'Removed provider'

/** Shared by option rows and chips: name (email when unnamed) plus clinic. */
function providerLabel(provider: ProviderResponse): {
  primary: string
  secondary: string
} {
  return {
    primary: provider.name || provider.email,
    secondary: provider.primary_clinic_name ?? 'No clinic',
  }
}

export function ProviderMultiSelect({
  providers,
  selectedIds,
  onChange,
  inputId,
  disabled = false,
}: ProviderMultiSelectProps) {
  const [query, setQuery] = useState('')

  const trimmedQuery = query.trim().toLowerCase()

  const filtered = trimmedQuery
    ? providers.filter((p) => {
        const { primary, secondary } = providerLabel(p)
        return (
          primary.toLowerCase().includes(trimmedQuery) ||
          p.email.toLowerCase().includes(trimmedQuery) ||
          secondary.toLowerCase().includes(trimmedQuery)
        )
      })
    : providers

  function handleChange(values: string[]) {
    onChange(values)
    setQuery('')
  }

  function removeChip(id: string) {
    onChange(selectedIds.filter((selectedId) => selectedId !== id))
  }

  return (
    <Combobox
      multiple
      immediate
      value={selectedIds}
      onChange={handleChange}
      disabled={disabled}
    >
      <div className="relative">
        <div className="flex flex-wrap items-center gap-1.5 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 focus-within:border-deep-purple focus-within:ring-2 focus-within:ring-lavender transition-colors">
          {selectedIds.map((id) => {
            // Iterate the selection, not `providers`, so an id whose provider
            // was deleted still shows up as a chip the admin can remove.
            const provider = providers.find((p) => p.id === id)
            const label = provider ? providerLabel(provider) : null
            const name = label ? label.primary : REMOVED_PROVIDER_LABEL
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 bg-soft-lavender text-deep-purple text-sm px-2 py-0.5 rounded"
              >
                {name}
                {label && (
                  <span className="text-gray-500">{` · ${label.secondary}`}</span>
                )}
                <button
                  type="button"
                  onClick={() => removeChip(id)}
                  disabled={disabled}
                  className="hover:text-charcoal transition-colors"
                  aria-label={`Remove ${name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )
          })}
          <ComboboxInput
            id={inputId}
            className="flex-1 min-w-[8rem] border-0 p-1 text-sm text-charcoal placeholder-gray-400 focus:outline-none focus:ring-0"
            placeholder={selectedIds.length > 0 ? '' : 'Search providers...'}
            onChange={(e) => setQuery(e.target.value)}
          />
          <ComboboxButton
            className="p-1 text-gray-400 hover:text-gray-600"
            aria-label="Show providers"
          >
            <ChevronDown className="w-4 h-4" />
          </ComboboxButton>
        </div>

        <ComboboxOptions className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg ring-1 ring-black/5 max-h-60 overflow-auto py-1 focus:outline-none">
          {filtered.map((provider) => {
            const { primary, secondary } = providerLabel(provider)
            return (
              <ComboboxOption
                key={provider.id}
                value={provider.id}
                className="flex items-center justify-between px-3 py-2 text-sm text-charcoal cursor-pointer data-[focus]:bg-gray-100"
              >
                <span className="min-w-0">
                  <span className="block truncate">{primary}</span>
                  <span className="block truncate text-xs text-gray-500">{secondary}</span>
                </span>
                {selectedIds.includes(provider.id) && (
                  <Check className="w-4 h-4 text-deep-purple shrink-0" />
                )}
              </ComboboxOption>
            )
          })}

          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">
              {providers.length === 0
                ? 'No providers yet.'
                : 'No providers match your search.'}
            </div>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  )
}

export default ProviderMultiSelect
